import debug from "debug";
const log = debug("app:objectidmap");

/**
 * Class for managing a mapping between object IDs and their corresponding UUIDs in a 3D scene.
 * @category Components
 * @subcategory UndoRedoManager
 */
export default class ObjectIdMap {
  /**
   * Constructor for ObjectIdMap.
   * @param {Object} experience - The experience object containing shared resources.
   * @param {Object} experience.eventHub - Event hub for emitting and listening to events.
   * @param {Object} experience.undoRedoVault - Vault containing the shared objectIdMap.
   * @param {Object} experience.xmdVault - Vault containing figure and other data.
   */
  constructor(experience) {
    /**
     * Reference to the experience object.
     * @type {Object}
     */
    this.experience = experience;

    /**
     * Event hub for emitting and listening to events.
     * @type {Object}
     */
    this.eventHub = experience.eventHub;

    /**
     * Shared object ID map from the undoRedoVault.
     * @type {Map<string, string>}
     */
    this.objectIdMap = experience.undoRedoVault.objectIdMap; // Reference the shared objectIdMap

    /**
     * Vault containing figure and other data.
     * @type {Object}
     */
    this.xmdVault = experience.xmdVault;

    // Setup the event listener to update the objectIdMap
    this.setupListeners();
  }

  /**
   * Sets up event listeners for updating the objectIdMap.
   */
  setupListeners() {
    // Listen for xmdUpdated events and update the objectIdMap accordingly
    this.eventHub.on("xmdUpdated", () => this.updateObjectIdMap());
  }

  cleanup() {
    if (!this.eventHub) return; // Prevent errors if already cleaned up

    log("Cleaning up ObjectIdMap...");

    // Unregister event listener for xmdUpdated event
    this.eventHub.off("xmdUpdated", this.updateObjectIdMap);

    // Optionally clear objectIdMap if resetting is required
    this.objectIdMap.clear();
    log("Cleared objectIdMap");

    // Clear references
    this.experience = null;
    this.eventHub = null;
    this.objectIdMap = null;
    this.xmdVault = null;

    log("ObjectIdMap cleanup completed.");
  }

  /**
   * Updates the objectIdMap by traversing the figures and others in the xmdVault.
   * Adds new entries or updates existing ones with the latest UUIDs.
   */
  updateObjectIdMap() {
    log("xmdUpdated event received");
    log("this.xmdvault", this.xmdVault);
    const { figures, others } = this.experience.xmdVault;
    log("figures, otehres", figures, others);

    // Traverse figures and update or add to the objectIdMap
    if (Array.isArray(figures)) {
      figures.forEach((figure) => {
        if (figure.partData && figure.partData.objectId) {
          // Check if objectId exists in the map and update if necessary
          if (!this.objectIdMap.has(figure.partData.objectId)) {
            this.objectIdMap.set(figure.partData.objectId, figure.uuid);
            log("Added new objectId to objectIdMap:", figure.partData.objectId);
          } else if (
            this.objectIdMap.get(figure.partData.objectId) !== figure.uuid
          ) {
            this.objectIdMap.set(figure.partData.objectId, figure.uuid);
            log("Updated uuid for objectId:", figure.partData.objectId);
          }
        } else {
          log("Figure missing objectId:", figure);
        }
      });
    }

    // Traverse others and update or add to the objectIdMap
    if (Array.isArray(others)) {
      others.forEach((other) => {
        if (other.objectId) {
          // Check if objectId exists in the map and update if necessary
          if (!this.objectIdMap.has(other.objectId)) {
            this.objectIdMap.set(other.objectId, other.uuid);
            log("Added new objectId to objectIdMap:", other.objectId);
          } else if (this.objectIdMap.get(other.objectId) !== other.uuid) {
            this.objectIdMap.set(other.objectId, other.uuid);
            log("Updated uuid for objectId:", other.objectId);
          }
        } else {
          log("Other object missing objectId:", other);
        }
      });
    }

    log("objectIdMap updated successfully", this.objectIdMap);
  }
}
